class ArtLaserMobileMenu extends HTMLElement {
  connectedCallback() {
    if (this.dataset.agMobileReady === 'true') return;
    this.dataset.agMobileReady = 'true';

    this.toggleButton = this.querySelector('[data-ag-mobile-toggle]');
    this.drawer = this.querySelector('.ag-mobile-menu__drawer');
    this.closeButton = this.querySelector('.ag-mobile-menu__close');
    this.closeTargets = [...this.querySelectorAll('[data-ag-mobile-close]')];
    this.topAccordions = [...this.querySelectorAll(':scope .ag-mobile-menu__nav > .ag-mobile-menu__list > .ag-mobile-menu__item > [data-ag-mobile-accordion]')];
    this.groupAccordions = [...this.querySelectorAll('[data-ag-mobile-group]')];
    this.duration = Number.parseFloat(getComputedStyle(this).getPropertyValue('--ag-mobile-duration')) || 320;
    this.closeTimer = null;

    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleResize = this.handleResize.bind(this);

    this.toggleButton?.addEventListener('click', () => this.openDrawer());
    this.closeTargets.forEach((target) => target.addEventListener('click', () => this.closeDrawer(true)));
    this.drawer?.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => this.closeDrawer(false));
    });

    this.topAccordions.forEach((details) => {
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      details.addEventListener('toggle', () => this.handleTopAccordion(details));
    });

    this.groupAccordions.forEach((details) => {
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      details.addEventListener('toggle', () => this.handleGroupAccordion(details));
    });

    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.handleResize);
  }

  openDrawer() {
    window.clearTimeout(this.closeTimer);
    this.removeAttribute('data-closing');
    this.setAttribute('open', '');
    this.toggleButton?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('ag-mobile-menu-is-open');

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.classList.add('is-open');
        window.setTimeout(() => {
          if (typeof window.trapFocus === 'function' && this.drawer) {
            window.trapFocus(this.drawer, this.closeButton || this.drawer);
          } else {
            this.closeButton?.focus();
          }
        }, this.duration);
      });
    });
  }

  closeDrawer(restoreFocus = true, immediate = false) {
    if (!this.hasAttribute('open')) return;

    window.clearTimeout(this.closeTimer);
    this.classList.remove('is-open');
    this.setAttribute('data-closing', 'true');
    this.toggleButton?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('ag-mobile-menu-is-open');

    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(restoreFocus ? this.toggleButton : false);
    } else if (restoreFocus) {
      this.toggleButton?.focus();
    }

    const finish = () => {
      this.removeAttribute('open');
      this.removeAttribute('data-closing');
      this.topAccordions.forEach((details) => details.removeAttribute('open'));
      this.groupAccordions.forEach((details) => details.removeAttribute('open'));
    };

    if (immediate) {
      finish();
    } else {
      this.closeTimer = window.setTimeout(finish, this.duration);
    }
  }

  handleTopAccordion(details) {
    const summary = details.querySelector(':scope > summary');
    summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false');

    if (!details.open) return;
    this.topAccordions.forEach((other) => {
      if (other !== details) other.removeAttribute('open');
    });

    window.setTimeout(() => {
      summary?.scrollIntoView({ block: 'nearest', behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });
    }, 60);
  }

  handleGroupAccordion(details) {
    const summary = details.querySelector(':scope > summary');
    summary?.setAttribute('aria-expanded', details.open ? 'true' : 'false');

    if (!details.open) return;
    const groupContainer = details.parentElement;
    groupContainer?.querySelectorAll(':scope > [data-ag-mobile-group]').forEach((other) => {
      if (other !== details) other.removeAttribute('open');
    });
  }

  handleKeydown(event) {
    if (event.key !== 'Escape' || !this.hasAttribute('open')) return;
    event.preventDefault();
    this.closeDrawer(true);
  }

  handleResize() {
    if (window.matchMedia('(min-width: 990px)').matches && this.hasAttribute('open')) {
      this.closeDrawer(false, true);
    }
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('ag-mobile-menu')) {
  customElements.define('ag-mobile-menu', ArtLaserMobileMenu);
}
